import { useCallback, useMemo } from "react";

import { useActionWorkflow, useServices } from "@/hooks";
import { ActionType } from "@/types";

export interface TaskContext {
  token: string;
  setMessage: (message: string) => void;
}

export interface RunOptions {
  success?: string;      // Snackbar shown when the task completes
  notifyError?: boolean; // Show the error in a snackbar as well as the action state
}

/**
 * Runs async audience tasks through the action workflow. Tasks receive an
 * access token and should throw on failure (see `unwrap`) so the action
 * state always reflects the outcome.
 */
export const useAudienceTask = () => {
  const { auth, ui } = useServices();
  const { action, runAction, resetAction } = useActionWorkflow();

  const run = useCallback((
    type: ActionType,
    message: string,
    task: (context: TaskContext) => Promise<void>,
    options: RunOptions = {}
  ) => runAction(type, message, async (setMessage) => {
    try {
      const token = await auth.getAccessTokenSilently();
      await task({ token, setMessage });
      if (options.success) ui.openSnackbar(options.success, "success");
    } catch (err: any) {
      if (options.notifyError) ui.openSnackbar(err.message, "error");
      throw err;
    }
  }), [auth, runAction, ui]);

  const isRunning = useCallback(
    (type?: ActionType) => action.status === 'loading' && (!type || action.type === type),
    [action]
  );

  const hasFailed = useCallback(
    (type?: ActionType) => action.status === 'error' && (!type || action.type === type),
    [action]
  );

  return useMemo(
    () => ({ action, run, reset: resetAction, isRunning, hasFailed }),
    [action, run, resetAction, isRunning, hasFailed]
  );
}
